import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import styles from './Breadcrumbs.module.scss';

/* ────────────────────────────────────────────────────────────
 * Breadcrumbs — hierarchy trail above the PageHeader.
 *
 * Required on every detail / edit screen (INVENTORY_UI_PLAN.md §6).
 * `items` is ordered ancestor → current. The last entry is the
 * current page and renders as a non-link span.
 *
 *   <Breadcrumbs items={[
 *     { label: 'Inventory', to: '/Stock' },
 *     { label: 'Stock', to: '/Stock' },
 *     { label: 'Cheese Slices' },         // current page
 *   ]} />
 *
 * Optional `showHome` adds a Home icon link at the start.
 * ──────────────────────────────────────────────────────────── */

const Breadcrumbs = ({ items, showHome, homeTo, className, ariaLabel }) => {
  const classNames = [styles.crumbs, className || ''].filter(Boolean).join(' ');

  return (
    <nav aria-label={ariaLabel} className={classNames}>
      <ol className={styles.list}>
        {showHome && (
          <li className={styles.item}>
            <Link to={homeTo} className={styles.link} aria-label="Home">
              <Home size={14} strokeWidth={1.75} />
            </Link>
            <ChevronRight
              size={14}
              strokeWidth={1.5}
              className={styles.sep}
              aria-hidden="true"
            />
          </li>
        )}
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <Fragment key={`${item.label}-${idx}`}>
              <li className={styles.item}>
                {isLast || !item.to ? (
                  <span
                    className={styles.current}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link to={item.to} className={styles.link}>
                    {item.label}
                  </Link>
                )}
                {!isLast && (
                  <ChevronRight
                    size={14}
                    strokeWidth={1.5}
                    className={styles.sep}
                    aria-hidden="true"
                  />
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
    })
  ).isRequired,
  showHome: PropTypes.bool,
  homeTo: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Breadcrumbs.defaultProps = {
  showHome: false,
  homeTo: '/',
  className: '',
  ariaLabel: 'Breadcrumb',
};

export default Breadcrumbs;
